import {
    cloneElement,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import Button, { type ButtonProps } from '~/components/new/Button';
import IconAction, { type IconActionProps } from '~/components/new/IconAction';
import { Menu, MenuRowAction } from '~/components/new/Menu';
import { useOnClickOutside } from '~/components/ui/hooks';
import { RemixIconNames, type IconNames } from '~/components/ui/Icon';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import { z } from '~/utils/zIndex';

export const moreOptionsContext = createContext<{ closeMenu: () => void }>({
    closeMenu: () => {},
});

type BaseMoreOptionsAlign = 'left' | 'right';
type BaseMoreOptionsVerticalAlign = 'top' | 'bottom';
type BaseMoreOptionsProps = {
    title: string;
    children: ReactNode;
    className?: string;
    align?: BaseMoreOptionsAlign;
    verticalAlign?: BaseMoreOptionsVerticalAlign;
    usePortal?: boolean;
    icon?: IconNames;
    triggerComponent: ReactElement<typeof Button | typeof HTMLButtonElement>;
    triggerRef?: RefObject<HTMLButtonElement>;
} & DataTestAttributes;

export function BaseMoreOptions({
    children,
    className = '',
    align = 'right',
    verticalAlign = 'bottom',
    title,
    usePortal = false,
    icon,
    triggerComponent,
    triggerRef,
    'data-test-component': testComponent = 'ui/MoreOptions',
    'data-test-name': testName,
}: BaseMoreOptionsProps) {
    const ref = useRef<HTMLDivElement>(null!);
    const menuRef = useRef<HTMLDivElement>(null!);
    const buttonRef = useRef<HTMLButtonElement>(null!);
    const [open, setOpen] = useState(false);

    // when usePortal is true, the outside click is handled by portal, otherwise menu closes before row item gets clicked
    useOnClickOutside(ref, () => {
        if (!usePortal) {
            setOpen(false);
        }
    });

    const triggerElement = useMemo(
        () =>
            // cloneElement only forwards the ref to the trigger, it never reads .current
            // eslint-disable-next-line react-hooks/refs
            cloneElement(triggerComponent, {
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(!open);
                },
                ref: buttonRef,
                title: open ? '' : title,
                icon: icon ?? (triggerComponent.props as any).icon,
            } as ButtonProps),
        [open],
    );

    // reusable menu component to render alone or as portal children
    const menuComponent = (
        <moreOptionsContext.Provider value={{ closeMenu: () => setOpen(false) }}>
            <Menu
                ref={menuRef}
                typology='action'
                role='menu'
                data-test-align={align}
                data-test-vertical-align={verticalAlign}
                className={classNames({
                    [`moreoptions-menu`]: true,
                    'moreoptions-menu-in-portal': usePortal,
                })}
                data-test-component={`${testComponent}/Menu`}
                data-test-name={testName}>
                {children}
            </Menu>
        </moreOptionsContext.Provider>
    );

    return (
        <div
            className={classNames({
                moreoptions: true,
                [className]: Boolean(className),
                'moreoptions-open': open,
            })}
            ref={ref}
            data-test-component={testComponent}
            data-test-name={testName}>
            {triggerElement}

            {open &&
                (usePortal ? (
                    <MoreOptionsPortal
                        anchorRef={triggerRef ?? buttonRef}
                        onClose={() => setOpen(false)}
                        align={align}
                        verticalAlign={verticalAlign}>
                        {menuComponent}
                    </MoreOptionsPortal>
                ) : (
                    menuComponent
                ))}
        </div>
    );
}

export type MoreOptionsProps = Omit<BaseMoreOptionsProps, 'triggerComponent' | 'size'> &
    Pick<ButtonProps, 'variant'> & {
        size?: 'small' | 'medium' | 'large';
        iconColorVar?: IconActionProps['colorVar'];
    };

export default function MoreOptions({
    title,
    size = 'medium',
    variant = 'floating',
    align = 'right',
    className = '',
    iconColorVar,
    ...props
}: MoreOptionsProps) {
    return size === 'small' ? (
        // IconAction trigger version to use it in ag-agrid actions column and Alert actions
        <BaseMoreOptions
            {...props}
            title={title ?? 'Actions'}
            className={classNames({
                'moreoptions-size-small': true,
                [className]: Boolean(className),
            })}
            usePortal
            align={align}
            triggerComponent={
                <IconAction
                    icon={props.icon || RemixIconNames.RiMoreFill}
                    colorVar={iconColorVar}
                    targetSize='small'
                    onClick={() => {}} // it will be overridden by BaseMoreOptions
                />
            }
        />
    ) : (
        <BaseMoreOptions
            {...props}
            title={title}
            className={className}
            align={align}
            usePortal
            icon={RemixIconNames.RiMoreFill}
            triggerComponent={
                <Button
                    action='primary'
                    icon={props.icon}
                    size={size}
                    variant={variant}
                />
            }
        />
    );
}

type MoreOptionsItemVariant = 'regular' | 'danger';
export type MoreOptionsItemProps = {
    label: string;
    onClick: (ev: MouseEvent<HTMLDivElement>) => void;
    icon?: IconNames;
    variant?: MoreOptionsItemVariant;
    disabled?: boolean;
} & DataTestAttributes;

const MoreOptionsItem = ({
    label,
    onClick,
    icon,
    variant = 'regular',
    disabled = false,
    'data-test-name': testName,
}: MoreOptionsItemProps) => {
    const { closeMenu } = useContext(moreOptionsContext);
    return (
        <MenuRowAction
            role='button'
            onClick={(e) => {
                closeMenu();
                onClick(e);
            }}
            negative={variant === 'danger'}
            icon={icon}
            label={label}
            disabled={disabled}
            data-test-name={testName}
        />
    );
};

MoreOptions.Item = MoreOptionsItem;

type MoreOptionsPortalProps = {
    anchorRef: React.RefObject<HTMLElement>;
    children: React.ReactNode;
    onClose: () => void;
    align?: BaseMoreOptionsAlign;
    verticalAlign?: BaseMoreOptionsVerticalAlign;
};

export const MoreOptionsPortal: React.FC<MoreOptionsPortalProps> = ({
    anchorRef,
    children,
    onClose,
    align = 'right',
    verticalAlign = 'bottom',
}) => {
    const [settings, setSettings] = useState<{
        top: number;
        left: number;
        parentWidth: number;
    }>({
        top: 0,
        left: 0,
        parentWidth: 0,
    });
    const MoreOptionsRef = useRef<HTMLElement>(null!);
    useOnClickOutside(MoreOptionsRef, onClose);

    useEffect(() => {
        const updatePosition = () => {
            const btnRect = anchorRef.current?.getBoundingClientRect();
            const menuRect = (
                MoreOptionsRef.current.firstChild as HTMLElement
            ).getBoundingClientRect();

            if (btnRect && menuRect) {
                const canOpenLeft = btnRect.right - menuRect.width > 0;
                const canOpenRight = btnRect.left + menuRect.width < window.innerWidth;
                const canOpenDown = btnRect.bottom + menuRect.height < window.innerHeight;
                const canOpenUp = btnRect.top - menuRect.height > 0;

                const menuPaddingFromTrigger = 4;

                setSettings({
                    // when verticalAlign is bottom should open down.
                    top:
                        (verticalAlign === 'bottom' && canOpenDown) || !canOpenUp
                            ? btnRect.bottom + window.scrollY + menuPaddingFromTrigger
                            : btnRect.top -
                              menuRect.height +
                              window.scrollY -
                              menuPaddingFromTrigger,

                    // when align is right should open to the left
                    left:
                        (align === 'right' && canOpenLeft) || !canOpenRight
                            ? btnRect.right - menuRect.width + window.scrollX
                            : btnRect.left + window.scrollX,

                    parentWidth: btnRect.width,
                });
            }
        };

        updatePosition();

        const controller = new AbortController();

        window.addEventListener('scroll', updatePosition, { signal: controller.signal });
        window.addEventListener('resize', updatePosition, { signal: controller.signal });

        return () => {
            controller.abort();
        };
    }, [onClose]);

    return createPortal(
        <div
            onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} // prevent conflict with modals
            ref={MoreOptionsRef as RefObject<HTMLDivElement>}
            className='RemotelyTheme'
            style={{
                position: 'absolute',
                zIndex: z.dropdown,
                top: settings.top,
                left: settings.left,
                minWidth: 240, // by design
            }}>
            {children}
        </div>,
        document.body,
    );
};
